// Service hook (TS-2.5.01, Resource Accessor layer). The projects+products join the roadmap page
// needs regardless of which Team/Product mode is showing (decisions.md D-280, superseding the half
// of D-278 that gated this read to Product mode only): the detail cards now resolve a product name
// and an effective business owner (D-279) on EVERY card, in both modes, so `RoadmapPage` fetches
// once here and hands the same `projects`/`products` down to both `ProductGroupedBoard` (which no
// longer fetches its own copy - see that component's own header) and the detail cards' owner join
// (lib/resolveProjectOwners.ts). Mirrors ProductGroupedBoard's former fetch shape exactly (full
// page via D-266/D-272's MAX_PAGE_SIZE ceiling; includeRetired products per D-268), just lifted one
// level up and de-duplicated to run once instead of per-toggle-mount.
import { useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { ProductRecord, ProjectSummary } from "../../../api/generated/contracts";
import { portfolioDataApi } from "../../../api/portfolioData";
import { useProducts } from "../../../hooks/useProducts";

// D-266/D-272's documented ceiling (PageDefaults.MaximumSize) - every project in one read, since
// both consumers (the board regroup and the card join) need the whole list, not a page of it.
const MAX_PAGE_SIZE = 500;

export interface UseProjectProductJoinResult {
  readonly projects: readonly ProjectSummary[] | null;
  readonly products: readonly ProductRecord[] | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

export function useProjectProductJoin(): UseProjectProductJoinResult {
  const [projects, setProjects] = useState<readonly ProjectSummary[] | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<ApiError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    void portfolioDataApi
      .listProjects({ pageSize: MAX_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setProjects(page.items);
        setProjectsError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProjectsError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The project list could not be loaded."));
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // D-268: includeRetired - a chip or card whose project already carries a retired product must
  // still resolve that product's name and business owner, not an orphaned key.
  const { products, loading: productsLoading, error: productsError } = useProducts(true);

  return {
    projects,
    products,
    loading: projectsLoading || productsLoading,
    error: projectsError ?? productsError,
  };
}
